import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, Upload, Check, Send, FileText, MessageSquare, Phone, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { icon: FileText, title: "Submit Complaint", description: "Fill out the form with details about your issue" },
  { icon: MessageSquare, title: "We Review", description: "Our team reviews your complaint within 24-48 hours" },
  { icon: Phone, title: "Contact Casino", description: "We reach out to the casino on your behalf" },
  { icon: CheckCircle2, title: "Resolution", description: "We work to resolve the issue and keep you updated" },
];

export default function Complaint() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    casino_name: "",
    title: "",
    details: "",
    screenshot: null,
  });
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setFileName(file.name);
      
      // Upload file
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm(prev => ({ ...prev, screenshot: response.data.file_id }));
        toast.success("Screenshot uploaded");
      } catch (error) {
        toast.error("Failed to upload file");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.casino_name || !form.title || !form.details) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/complaints`, form);
      setSubmitted(true);
      toast.success("Complaint submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit complaint. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center" data-testid="complaint-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">Complaint Submitted!</h1>
          <p className="text-white/60 mb-8">
            Thank you for submitting your complaint. Our team will review it and get back to you within 24-48 hours.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setForm({ name: "", email: "", casino_name: "", title: "", details: "", screenshot: null });
              setFileName("");
            }}
            className="gradient-neon text-white rounded-full"
          >
            Submit Another Complaint
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Submit a Casino Complaint – PlaySpiro</title>
        <meta name="description" content="Had a problem with an online casino? Submit your complaint to PlaySpiro and our team will help resolve it." />
        <link rel="canonical" href="https://playspiro.com/complaint" />
      </Helmet>
    <div className="min-h-screen py-8" data-testid="complaint-page">
      {/* Header */}
      <section className="gradient-hero py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            Submit a <span className="text-primary">Complaint</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Having issues with an online casino? We're here to help resolve your disputes 
            and ensure fair treatment for all players.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4">
        {/* Process Steps */}
        <div className="glass rounded-2xl p-6 md:p-8 mb-8">
          <h2 className="text-xl font-bold text-white mb-6 text-center font-['Montserrat']">
            How It Works
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center relative">
                  <step.icon className="w-6 h-6 text-primary" />
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-secondary text-black text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                <p className="text-white/50 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Complaint Form */}
        <div className="glass rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-6 h-6 text-orange-500" />
            <h2 className="text-2xl font-bold text-white font-['Montserrat']">Complaint Form</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white/70 text-sm mb-2">Your Name *</label>
                <Input
                  placeholder="John Doe"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white h-12"
                  data-testid="complaint-name-input"
                  required
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Your Email *</label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white h-12"
                  data-testid="complaint-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Casino Name *</label>
              <Input
                placeholder="Enter the casino name"
                value={form.casino_name}
                onChange={(e) => setForm(prev => ({ ...prev, casino_name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white h-12"
                data-testid="complaint-casino-input"
                required
              />
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Complaint Title *</label>
              <Input
                placeholder="Brief summary of your issue"
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                className="bg-white/5 border-white/10 text-white h-12"
                data-testid="complaint-title-input"
                required
              />
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Complaint Details *</label>
              <Textarea
                placeholder="Please provide as much detail as possible about your issue, including dates, amounts, and any communication with the casino..."
                value={form.details}
                onChange={(e) => setForm(prev => ({ ...prev, details: e.target.value }))}
                className="bg-white/5 border-white/10 text-white min-h-[200px]"
                data-testid="complaint-details-input"
                required
              />
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Screenshot (Optional)</label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="screenshot-upload"
                  data-testid="complaint-screenshot-input"
                />
                <label
                  htmlFor="screenshot-upload"
                  className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-primary/50 transition-colors"
                >
                  {fileName ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-5 h-5" />
                      <span>{fileName}</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-white/40" />
                      <span className="text-white/60">Click to upload screenshot (max 5MB)</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-glow gradient-neon text-white font-bold rounded-full h-14 text-lg"
                data-testid="submit-complaint-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" /> Submit Complaint
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
      </>
  );
}